<template>
  <pre v-if="isDevelopment && false">
    <div class="dev-container">
      <p>(Development mode)</p>
      <code>{{ error?.message }}</code>
      <code v-html="error?.stack"></code>
    </div>
  </pre>
  <div v-else-if="!!storyRes?.story && !!settings" :class="storyRes.story.content.is_dark ? 'is-dark' : 'is-light'">
    <BMHeader
      v-if="typeof header !== 'string' && header?.content"
      :blok="header.content"
      :inPageBlok="storyRes.story.content.header_in_page"
      :alternates="storyRes.story.alternates" />
    <CorePage v-if="settings?.content" :blok="storyRes.story.content" :settings="settings.content" />
    <BMFooter
      v-if="
        settings?.content.footer_default &&
        typeof settings.content.footer_default !== 'string' &&
        settings?.content.footer_default?.content
      "
      :blok="settings.content.footer_default.content"
      :isDark="storyRes.story.content.is_dark" />
    <BaseModalVideo :youtubePrivacyWarning="settings.content.youtube_privacy_warning" />
  </div>
  <div v-else>
    <!-- In case the Storyblok error story can not be found this is the fallback -->
    <div class="container-full">
      <h1 class="heading-main-large" :style="{ marginTop: '128px' }">Error 404</h1>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app';
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useStoryblokBridge } from '@storyblok/vue';
import { useBreakpoint, useScroll, useSeoMetaTags, useStoryblokClient } from '@seb-platform/shared/composables';
import { defaultStoryParams } from '@seb-platform/shared/types/storyblok';
import { BaseModalVideo } from '~/components/base';
import BMFooter from '~/components/storyblok/basic/BMFooter/BMFooter.vue';
import BMHeader from '~/components/storyblok/basic/BMHeader/BMHeader.vue';
import CorePage from '~/components/storyblok/core/CorePage/CorePage.vue';
import { LOCALE_TO_HREFLANG_MAP } from '~/types/locales';
import { useLocale } from './composables/useLocale';
import type { CorePageStoryblok, CoreSettingsStoryblok } from './types/storyblok-generated';

defineProps({
  error: Object as () => NuxtError,
});

const { locale } = useLocale();
const { getStory, getSettings } = await useStoryblokClient();

const isDevelopment = import.meta.dev;
const settings = await getSettings<CoreSettingsStoryblok>(locale);
const storyRes = await getStory<CorePageStoryblok>(`/${locale}/error-page`);

const { setScroll } = useScroll();
const { init: breakpointInit } = useBreakpoint();

function onScroll() {
  setScroll(window.scrollY);
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
});

onMounted(() => {
  window.addEventListener('scroll', onScroll);
});

onMounted(() => {
  breakpointInit();
  useStoryblokBridge(storyRes.value?.story?.id, evStory => (storyRes.value.story = evStory), defaultStoryParams);
});

/**
 * Get relevant header and alert from story or settings
 */
const header = computed(() => {
  if (storyRes.value?.story.content.header_alternative?.length) {
    return storyRes.value.story.content.header_alternative[0];
  }
  return settings.value.content.header_rich_default;
});

/**
 * Add SEO meta tags
 */
useSeoMetaTags(locale, storyRes.value.story, LOCALE_TO_HREFLANG_MAP);
</script>

<style lang="scss" scoped>
@use '~/assets/scss/variables';

.is-dark {
  background-color: variables.$brown-light;
}

.is-light {
  background-color: variables.$white;
}

.dev-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  padding: 20px;
  margin: 0 auto;
  max-width: 640px;
  background-color: variables.$red;
  color: variables.$white;
  font-family: monospace;
  white-space: pre-wrap;
  overflow: auto;
}
</style>
